// ================================
// src/components/signal/ProPanel.tsx — PRO PANEL FINAL
// - Multi-TF snapshot + summary + Entry/SL/ATR
// - S/R · Walls · Pivot · Fib toggles + Apply
// - 📝 Commentary (불릿, 줄분리) + 🧠 From AI (dialogue)
// - Volume walls: 카드형 그리드 (모바일 1열)
// - Footer disclaimer (학습 목적 문구) 하단 고정
// ================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Analysis as AType,
  confidence as confidenceRaw,
  volumeWalls,
  Candle,
} from "../../utils/chartUtils";

/* ===== Utils ===== */
const fmt = (x: any, d = 2) => {
  const n = Number(x);
  return Number.isFinite(n)
    ? n.toLocaleString(undefined, { maximumFractionDigits: d })
    : "-";
};
const safeNumber = (x: any, fallback = 0) =>
  Number.isFinite(Number(x)) ? Number(x) : fallback;

/* ===== Types ===== */
type Props = {
  symbol?: string;
  hasPro: boolean;
  analysis: AType | null;
  candles: Candle[];
  onUnlockByAd: () => void | Promise<void>;
  opt: { sr: boolean; walls: boolean; pivot: boolean; fib: boolean };
  setOpt: (next: Partial<Props["opt"]>) => void;
  applyGuideLines: () => void; // 상위에서 실제 차트 라인 적용 구현
};

type Dir = "long" | "short" | "wait";
type TF = "1m" | "15m" | "1h" | "2h" | "4h" | "1d";
type Mini = { tf: TF; dir: Dir; conf: number; rr: number };

/* ===== Multi-TF 분석 ===== */
const TF_MAP: Record<TF, string> = {
  "1m": "1m",
  "15m": "15m",
  "1h": "1h",
  "2h": "2h",
  "4h": "4h",
  "1d": "1d",
};
const GRID_TFS: TF[] = ["1m", "15m", "1h", "2h", "4h", "1d"];
const BINANCE = "https://api.binance.com";
const REFRESH_MS: Record<TF, number> = {
  "1m": 3_000,
  "15m": 180_000,
  "1h": 600_000,
  "2h": 1_200_000,
  "4h": 2_400_000,
  "1d": 14_400_000,
};

async function fetchKlines(
  symbol: string,
  tf: TF,
  limit = 400
): Promise<Candle[]> {
  const url = `${BINANCE}/api/v3/klines?symbol=${encodeURIComponent(
    symbol
  )}&interval=${TF_MAP[tf]}&limit=${Math.min(1000, Math.max(120, limit))}`;
  const r = await fetch(url);
  if (!r.ok) throw new Error(`klines ${r.status}`);
  const arr: any[] = await r.json();
  return arr.map((row) => ({
    time: Math.floor(row[0] / 1000),
    open: +row[1],
    high: +row[2],
    low: +row[3],
    close: +row[4],
    volume: +row[5],
  }));
}

const ema = (arr: number[], p: number) => {
  if (!arr.length || p <= 1) return NaN;
  const k = 2 / (p + 1);
  let prev = arr[0];
  for (let i = 1; i < arr.length; i++) prev = arr[i] * k + prev * (1 - k);
  return prev;
};

const atr = (c: Candle[], p = 14) => {
  if (c.length < 2) return 0;
  const tr: number[] = [];
  for (let i = 1; i < c.length; i++) {
    const a = c[i],
      b = c[i - 1];
    tr.push(
      Math.max(
        a.high - a.low,
        Math.abs(a.high - b.close),
        Math.abs(a.low - b.close)
      )
    );
  }
  const m = Math.min(p, tr.length);
  return m ? tr.slice(-m).reduce((s, v) => s + v, 0) / m : 0;
};

const analyzeArray = (cs: Candle[]): { dir: Dir; score: number; rr: number } => {
  if (!cs || cs.length < 60) return { dir: "wait", score: 0, rr: 0 };
  const closes = cs.map((c) => c.close);
  const last = cs[cs.length - 1];
  const E50 = ema(closes, 50),
    E200 = ema(closes, 200),
    A = atr(cs, 14);
  const dir: Dir =
    last.close > E50 && last.close > E200
      ? "long"
      : last.close < E50 && last.close < E200
      ? "short"
      : "wait";
  const entry = last.close;
  const stop =
    dir === "long" ? entry - 1.2 * A : dir === "short" ? entry + 1.2 * A : entry;
  const tp1 =
    dir === "long" ? entry + 1.0 * A : dir === "short" ? entry - 1.0 * A : entry;
  const risk = Math.abs(entry - stop),
    rew1 = Math.abs(tp1 - entry);
  const rr = risk > 0 ? rew1 / risk : 0;
  const slope = (E50 - E200) / (entry || 1);
  const pos = dir === "long" ? 1 : dir === "short" ? -1 : 0;
  const score = Math.max(-1, Math.min(1, pos * 0.7 + slope * 5)) * 100;
  return { dir, score, rr };
};

/* ===== AI Expert Dialogue ===== */
function buildExpertDialogue(
  t: (k: string) => string,
  args: {
    symbol: string;
    analysis: AType | null;
    price: number | null;
    atrVal: number;
    rr1Dyn: number;
    rr2Dyn: number;
    stopPx: number;
    tp1Px: number;
    tp2Px: number;
    bandLo: number;
    bandHi: number;
    emaSlope: number | null;
    volTag: string;
    dS_atr: number | null;
    dR_atr: number | null;
    confluence: string[];
    mini: Mini[] | null;
    confPct: number | null;
  }
) {
  const {
    symbol,
    analysis,
    price,
    atrVal,
    rr1Dyn,
    rr2Dyn,
    stopPx,
    tp1Px,
    tp2Px,
    bandLo,
    bandHi,
    emaSlope,
    volTag,
    dS_atr,
    dR_atr,
    confluence,
    mini,
    confPct,
  } = args;

  const dirTxt =
    analysis?.dir === "long"
      ? t("trend.long")
      : analysis?.dir === "short"
      ? t("trend.short")
      : t("trend.wait");

  const slopeTxt = Number.isFinite(emaSlope as number)
    ? `${fmt((emaSlope as number) * 100, 2)}%`
    : t("slope.mixed");

  const srNear =
    Number.isFinite(dS_atr as number) && (dS_atr as number) < 0.5
      ? t("near.s")
      : Number.isFinite(dR_atr as number) && (dR_atr as number) < 0.5
      ? t("near.r")
      : t("near.none");

  const inBand =
    Number.isFinite(bandLo) &&
    Number.isFinite(bandHi) &&
    Number.isFinite(price as number) &&
    (price as number) >= bandLo &&
    (price as number) <= bandHi
      ? t("band.inside")
      : t("band.outside");

  const tfLine =
    mini && mini.length
      ? mini
          .map((m) => {
            const tag =
              m.dir === "long"
                ? t("tag.long")
                : m.dir === "short"
                ? t("tag.short")
                : t("tag.wait");
            return `${m.tf}:${tag}(${fmt(m.conf, 0)}%,${fmt(m.rr, 2)}R)`;
          })
          .join(" · ")
      : t("tf.none");

  const confLine = confPct != null ? `${fmt(confPct, 0)}%` : "n/a";
  const conflLine = confluence.length
    ? confluence.join(", ")
    : t("no.confluence");

  const q1 =
    t("ai.q1")?.replace?.("{{symbol}}", symbol) ??
    `What’s the setup on ${symbol}?`;
  const a1 = (
    t("ai.a1") ||
    "Direction: {{dir}} · Volatility: {{vol}} · Slope: {{slope}} · S/R: {{sr}} · Band: {{band}}"
  )
    .replace("{{dir}}", dirTxt)
    .replace("{{vol}}", volTag)
    .replace("{{slope}}", slopeTxt)
    .replace("{{sr}}", srNear)
    .replace("{{band}}", inBand)
    .replace("{{label.volatility}}", t("label.volatility"))
    .replace("{{label.slope}}", t("label.slope"));

  const q2 = t("ai.q2") || "How do multiple timeframes look?";
  const a2 = (t("ai.a2") || "TF: {{tf}} · Confidence: {{conf}} · Confluence: {{confl}}")
    .replace("{{tf}}", tfLine)
    .replace("{{conf}}", confLine)
    .replace("{{confl}}", conflLine);

  const q3 = t("ai.q3") || "Risk/Reward?";
  const a3 = (
    t("ai.a3") ||
    "SL {{stop}}, TP1 {{tp1}}, TP2 {{tp2}} · RR {{rr1}}/{{rr2}}"
  )
    .replace("{{stop}}", fmt(stopPx, 4))
    .replace("{{tp1}}", fmt(tp1Px, 4))
    .replace("{{tp2}}", fmt(tp2Px, 4))
    .replace("{{rr1}}", fmt(rr1Dyn, 2))
    .replace("{{rr2}}", fmt(rr2Dyn, 2));

  return [
    { who: t("ai.label.you") || "You", text: q1 },
    { who: t("ai.label.analyst") || "Analyst", text: a1 },
    { who: t("ai.label.you") || "You", text: q2 },
    { who: t("ai.label.analyst") || "Analyst", text: a2 },
    { who: t("ai.label.you") || "You", text: q3 },
    { who: t("ai.label.analyst") || "Analyst", text: a3 },
  ];
}

/* ===== Wrapper ===== */
const ProPanel: React.FC<Props> = (props) => {
  if (!props.hasPro) return <ProLocked onUnlockByAd={props.onUnlockByAd} />;
  return <ProUnlocked {...props} />;
};
export default ProPanel;

/* ===== Locked ===== */
const ProLocked: React.FC<{ onUnlockByAd: Props["onUnlockByAd"] }> = ({
  onUnlockByAd,
}) => {
  const { t } = useTranslation("signal");
  return (
    <div className="card procard">
      <div className="card-head neon">{t("pro.title")}</div>
      <p className="muted">{t("pro.desc")}</p>
      <div className="gate-row">
        <button className="btn ad" onClick={onUnlockByAd}>
          ⏯ {t("pro.ad")}
        </button>
      </div>
      <style>{CARD_CSS}</style>
    </div>
  );
};

/* ===== Unlocked ===== */
const ProUnlocked: React.FC<Props> = ({
  symbol: propSymbol,
  analysis,
  candles,
  opt,
  setOpt,
  applyGuideLines,
}) => {
  const { t } = useTranslation("signal");
  const activeSymbol = propSymbol ?? (analysis as any)?.symbol ?? "BTCUSDT";

  // 가격/플랜
  const last = candles?.[candles.length - 1] as Candle | undefined;
  const price = Number.isFinite(last?.close) ? (last!.close as number) : null;
  const A = safeNumber(analysis?.atr, 0);
  const entryPx = safeNumber(analysis?.plan?.entry ?? price, price ?? 0);

  const dir: Dir = analysis?.dir ?? "wait";
  const stopPx =
    dir === "long"
      ? entryPx - 1.2 * A
      : dir === "short"
      ? entryPx + 1.2 * A
      : entryPx;
  const tp1Px =
    dir === "long"
      ? entryPx + 1.0 * A
      : dir === "short"
      ? entryPx - 1.0 * A
      : entryPx;
  const tp2Px =
    dir === "long"
      ? entryPx + 2.0 * A
      : dir === "short"
      ? entryPx - 2.0 * A
      : entryPx;

  const riskAbs = Math.abs(entryPx - stopPx) || 1e-9;
  const rr1Dyn = Math.abs(tp1Px - entryPx) / riskAbs;
  const rr2Dyn = Math.abs(tp2Px - entryPx) / riskAbs;

  const confRaw: any = confidenceRaw?.(
    analysis?.score ?? 0,
    analysis?.rr1 ?? rr1Dyn
  );
  const confPct: number | null =
    typeof confRaw === "number"
      ? confRaw
      : Number.isFinite(confRaw?.pct)
      ? Number(confRaw.pct)
      : null;
  const confTag: string | null =
    confRaw && typeof confRaw === "object" && typeof confRaw.tag === "string"
      ? confRaw.tag
      : null;

  const bandLo = safeNumber(analysis?.plan?.band?.[0], NaN);
  const bandHi = safeNumber(analysis?.plan?.band?.[1], NaN);

  // EMA slope
  const emaSlope = useMemo(() => {
    const e50 = safeNumber(analysis?.ema50, NaN);
    const e200 = safeNumber(analysis?.ema200, NaN);
    if (!Number.isFinite(e50) || !Number.isFinite(e200) || e200 === 0)
      return NaN;
    return (e50 - e200) / Math.abs(e200);
  }, [analysis?.ema50, analysis?.ema200]);

  // S/R 거리 (ATR 단위)
  const dS = Number.isFinite(analysis?.sup)
    ? entryPx - (analysis!.sup as number)
    : NaN;
  const dR = Number.isFinite(analysis?.res)
    ? (analysis!.res as number) - entryPx
    : NaN;
  const dS_atr =
    Number.isFinite(dS) && A > 0 ? (dS as number) / A : NaN;
  const dR_atr =
    Number.isFinite(dR) && A > 0 ? (dR as number) / A : NaN;

  // Confluence
  const conflLab = {
    pivotP: t("confl.pivot.p"),
    r1: t("confl.pivot.r1"),
    s1: t("confl.pivot.s1"),
    r2: t("confl.pivot.r2"),
    s2: t("confl.pivot.s2"),
    fib0382: t("confl.fib.0382"),
    fib0500: t("confl.fib.0500"),
    fib0618: t("confl.fib.0618"),
    fib0786: t("confl.fib.0786"),
  };
  const confluence: string[] = useMemo(() => {
    const out: string[] = [];
    const near = (lv?: number | null) =>
      Number.isFinite(lv) && A > 0
        ? Math.abs((lv as number) - entryPx) <= 0.5 * A
        : false;
    if (analysis?.piv) {
      const { P, R1, S1, R2, S2 } = analysis.piv;
      if (near(P)) out.push(conflLab.pivotP);
      if (near(R1)) out.push(conflLab.r1);
      if (near(S1)) out.push(conflLab.s1);
      if (near(R2)) out.push(conflLab.r2);
      if (near(S2)) out.push(conflLab.s2);
    }
    if (analysis?.fib) {
      const { hi, lo, ...levels } = analysis.fib as any;
      const addIf = (k: string, label: string) => {
        const v = levels?.[k];
        if (near(v)) out.push(label);
      };
      addIf("0.382", conflLab.fib0382);
      addIf("0.5", conflLab.fib0500);
      addIf("0.618", conflLab.fib0618);
      addIf("0.786", conflLab.fib0786);
    }
    return out;
  }, [analysis?.piv, analysis?.fib, A, entryPx, conflLab]);

  // Volume walls
  const walls = useMemo(
    () => volumeWalls(candles || [], 120, 5),
    [candles]
  );

  // TF snapshot polling
  const [mini, setMini] = useState<Mini[] | null>(null);
  const miniRef = useRef<Record<TF, Mini>>({});
  useEffect(() => {
    let alive = true;
    const timers: number[] = [];
    const refresh = async (tf: TF) => {
      if (!alive) return;
      try {
        const cs = await fetchKlines(activeSymbol, tf, 400);
        const a = analyzeArray(cs);
        const confVal =
          typeof confidenceRaw === "function"
            ? Number(confidenceRaw(a.score, a.rr) as any)
            : Math.round((Math.abs(a.score) / 100) * 100);
        miniRef.current[tf] = {
          tf,
          dir: a.dir,
          rr: a.rr,
          conf: Math.max(
            0,
            Math.min(
              100,
              Number.isFinite(confVal) ? confVal : 0
            )
          ),
        };
        const next = GRID_TFS.map((k) => miniRef.current[k]).filter(
          Boolean
        ) as Mini[];
        if (alive) setMini(next.length ? next : null);
      } catch {}
    };
    GRID_TFS.forEach(refresh);
    GRID_TFS.forEach((tf) =>
      timers.push(
        window.setInterval(() => refresh(tf), REFRESH_MS[tf])
      )
    );
    return () => {
      alive = false;
      timers.forEach(clearInterval);
    };
  }, [activeSymbol]);

  const isLoading = !analysis || price == null;

  // 대화형 해설
  const dialogue = useMemo(
    () =>
      buildExpertDialogue(t as any, {
        symbol: activeSymbol,
        analysis,
        price,
        atrVal: A,
        rr1Dyn,
        rr2Dyn,
        stopPx,
        tp1Px,
        tp2Px,
        bandLo,
        bandHi,
        emaSlope: Number.isFinite(emaSlope)
          ? (emaSlope as number)
          : null,
        volTag:
          typeof analysis?.volReg === "object"
            ? analysis?.volReg?.tag ?? "-"
            : analysis?.volReg ?? "-",
        dS_atr: Number.isFinite(dS_atr)
          ? (dS_atr as number)
          : null,
        dR_atr: Number.isFinite(dR_atr)
          ? (dR_atr as number)
          : null,
        confluence,
        mini,
        confPct,
      }),
    [
      t,
      activeSymbol,
      analysis,
      price,
      A,
      rr1Dyn,
      rr2Dyn,
      stopPx,
      tp1Px,
      tp2Px,
      bandLo,
      bandHi,
      emaSlope,
      dS_atr,
      dR_atr,
      confluence,
      mini,
      confPct,
    ]
  );

  return (
    <div className="card procard">
      <div className="card-head neon">{t("pro.title")}</div>

      {/* TF Snapshot */}
      <div className="tfgrid">
        {!mini ? (
          <div className="muted">{t("loading")}</div>
        ) : (
          <div className="tfwrap">
            {mini.map((m) => {
              const st =
                m.dir === "long"
                  ? "good"
                  : m.dir === "short"
                  ? "bad"
                  : "wait";
              const tag =
                m.dir === "long"
                  ? t("tag.long")
                  : m.dir === "short"
                  ? t("tag.short")
                  : t("tag.wait");
              return (
                <div className="tfcard" key={m.tf}>
                  <div className="tfrow">
                    <b className="tfl">{m.tf}</b>
                    <span className={`pill ${st}`}>{tag}</span>
                  </div>
                  <div className="bar white">
                    <div
                      className={`fill ${st}`}
                      style={{ width: `${m.conf}%` }}
                    />
                  </div>
                  <div className="tfs">
                    <span>
                      {t("pro.conf")} <b>{fmt(m.conf, 0)}%</b>
                    </span>
                    <span>
                      {t("rr.short")} {fmt(m.rr, 2)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Snapshot summary (RR · Confidence · Volatility) */}
      {!isLoading && (
        <div className="mini tf-summary">
          <div className="mk">{t("pro.summary")}</div>
          <div className="mv">
            {t("rr.short")}{" "}
            <b>
              {fmt(analysis?.rr1 ?? rr1Dyn, 2)} /{" "}
              {fmt(analysis?.rr2 ?? rr2Dyn, 2)}
            </b>
            {" · "}
            {t("pro.conf")}{" "}
            <b>
              {confPct != null
                ? `${fmt(confPct, 0)}%`
                : confTag ?? "n/a"}
            </b>
            {" · "}
            {t("label.volatility")}{" "}
            <b className="band-color">
              {typeof analysis?.volReg === "object"
                ? analysis?.volReg?.tag ?? "-"
                : analysis?.volReg ?? "-"}
            </b>
          </div>
        </div>
      )}

      {/* Metric cards — Entry / Stop / ATR */}
      {!isLoading && (
        <div className="cards">
          <div className="minigrid">
            <div className="mini" style={{ borderColor: "#204a7a" }}>
              <div className="mk">{t("entry")}</div>
              <div className="mv">{fmt(entryPx, 4)}</div>
            </div>
            <div className="mini" style={{ borderColor: "#7f1d1d" }}>
              <div className="mk">
                {t("stop")} (×1.20 ATR)
              </div>
              <div className="mv" style={{ color: "#ef4444" }}>
                {fmt(stopPx, 4)}
              </div>
              <div className="ms">
                {t("rr.short")} {fmt(rr1Dyn, 2)}/{fmt(rr2Dyn, 2)}
              </div>
            </div>
            <div className="mini" style={{ borderColor: "#3b3f5c" }}>
              <div className="mk">{t("atr")}</div>
              <div className="mv">{fmt(A, 4)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Options / Apply */}
      <div
        className="optrow pro"
        style={{ gap: 10, flexWrap: "wrap" }}
      >
        <button
          className={`icon ${opt.sr ? "on" : ""}`}
          onClick={() => setOpt({ sr: !opt.sr })}
          title={t("opt.sr")}
        >
          {t("opt.sr")}
        </button>
        <button
          className={`icon ${opt.walls ? "on" : ""}`}
          onClick={() => setOpt({ walls: !opt.walls })}
          title={t("opt.walls")}
        >
          {t("opt.walls.short")}
        </button>
        <button
          className={`icon ${opt.pivot ? "on" : ""}`}
          onClick={() => setOpt({ pivot: !opt.pivot })}
          title={t("opt.pivot")}
        >
          {t("opt.pivot.short")}
        </button>
        <button
          className={`icon ${opt.fib ? "on" : ""}`}
          onClick={() => setOpt({ fib: !opt.fib })}
          title={t("opt.fib")}
        >
          {t("opt.fib.short")}
        </button>
        <button
          className="mini apply-btn"
          onClick={applyGuideLines}
          title="Apply guide lines to chart (labels: white/yellow)"
        >
          {t("pro.apply")}
        </button>
      </div>

      {/* 📝 해설 & 결론 */}
      {!isLoading && (
        <details className="acc" open>
          <summary>📝 {t("pro.commentary")}</summary>
          <div className="mini" style={{ marginTop: 8 }}>
            <div className="mk">From AI</div>
            <div className="mv">
              <b
                className={
                  dir === "long"
                    ? "good"
                    : dir === "short"
                    ? "bad"
                    : "wait"
                }
              >
                {dir === "long"
                  ? t("trend.long")
                  : dir === "short"
                  ? t("trend.short")
                  : t("trend.wait")}
              </b>
              {" · "}
              {t("label.slope")}{" "}
              {Number.isFinite(emaSlope)
                ? `${fmt((emaSlope as number) * 100, 2)}%`
                : t("slope.mixed")}
              {" · "}
              {t("label.volatility")}:{" "}
              {typeof analysis?.volReg === "object"
                ? analysis?.volReg?.tag ?? "-"
                : analysis?.volReg ?? "-"}
              {Number.isFinite(bandLo) &&
              Number.isFinite(bandHi) &&
              Number.isFinite(entryPx) &&
              entryPx >= bandLo &&
              entryPx <= bandHi
                ? ` · ${t("band.inside")}`
                : ` · ${t("band.outside")}`}
            </div>
          </div>

          <ul className="kv">
            {/* S/R 근접도 */}
            <li className="kv-block">
              <div className="kv-label">{t("sr.proximity")}</div>
              <div className="kv-body">
                <div>
                  {Number.isFinite(dS_atr)
                    ? `${t("support")} ${fmt(
                        analysis!.sup as number,
                        4
                      )} (${fmt(dS_atr, 2)} ATR)`
                    : "-"}
                </div>
                <div>
                  {Number.isFinite(dR_atr)
                    ? `${t("resistance")} ${fmt(
                        analysis!.res as number,
                        4
                      )} (${fmt(dR_atr, 2)} ATR)`
                    : "-"}
                </div>
                <div className="kv-note">
                  {(() => {
                    const nearS =
                      Number.isFinite(dS_atr) &&
                      (dS_atr as number) >= 0 &&
                      (dS_atr as number) < 0.5;
                    const nearR =
                      Number.isFinite(dR_atr) &&
                      (dR_atr as number) >= 0 &&
                      (dR_atr as number) < 0.5;
                    return nearS
                      ? t("near.s")
                      : nearR
                      ? t("near.r")
                      : t("near.none");
                  })()}
                </div>
              </div>
            </li>

            {/* Confluence */}
            <li className="kv-block">
              <div className="kv-label">{t("confluence")}</div>
              <div className="kv-body">
                {confluence.length
                  ? confluence.join(", ")
                  : t("no.confluence")}
              </div>
            </li>

            {/* Trade math */}
            <li className="kv-block">
              <div className="kv-label">{t("trade.math")}</div>
              <div className="kv-body">
                <div>
                  {t("stop")} {fmt(stopPx, 4)}
                </div>
                <div>
                  {t("tp1")} {fmt(tp1Px, 4)} · {t("tp2")}{" "}
                  {fmt(tp2Px, 4)}
                </div>
                <div className="kv-note">
                  {t("rr.short")} {fmt(rr1Dyn, 2)}/{fmt(rr2Dyn, 2)}{" "}
                  {confPct != null
                    ? `· ${t("pro.conf")} ${fmt(
                        confPct,
                        0
                      )}%`
                    : ""}
                </div>
              </div>
            </li>
          </ul>

          {/* Footer disclaimer */}
          <div className="footer-disclaimer">{t("ai.footer")}</div>
        </details>
      )}

      {/* 🧠 From AI — Dialogue */}
      {!isLoading && (
        <details className="acc" open>
          <summary>🧠 From AI</summary>
          <div className="dialog">
            {(dialogue || []).map((turn, idx) => (
              <div
                key={idx}
                className={`drow ${
                  turn.who === (t("ai.label.analyst") || "Analyst")
                    ? "analyst"
                    : "you"
                }`}
              >
                <span className="dw">{turn.who}:</span>
                <span className="dt">{turn.text}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Volume Walls */}
      {!isLoading && (
        <details className="acc">
          <summary>🧱 {t("pro.walls")}</summary>
          <ul className="wall-list">
            {walls.map((w: any, i: number) => {
              const vol =
                w?.vol ?? w?.size ?? w?.qty ?? w?.volume ?? 0;
              return (
                <li
                  key={`${w?.price ?? i}-${i}`}
                  className="wall-card"
                >
                  <div className="wall-row">
                    <span className="wall-rank">
                      #{i + 1}
                    </span>
                    <span className="wall-price">
                      {fmt(w?.price, 4)}
                    </span>
                  </div>
                  <div className="wall-row wall-sub">
                    <span>{t("abbr.vol")}</span>
                    <span className="wall-vol">
                      ≈ {fmt(vol, 0)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </details>
      )}

      <style>{CARD_CSS}</style>
    </div>
  );
};

/* ===== CSS ===== */
const CARD_CSS = `
.procard .neon{color:#a5f3fc;text-shadow:0 0 8px #0ea5e911}
.procard .muted{color:#9fb1c3}
.procard .btn.ad{border:1px solid #2a3344;border-radius:10px;padding:8px 12px;font-weight:800;background:#1d4ed8;color:#e5eaf3}
.procard .gate-row{margin-top:8px}

.procard .good{color:#16a34a}.procard .bad{color:#e11d48}.procard .wait{color:#e5e7eb}

/* 토글/적용 버튼 색상 강제 */
.procard .icon{
  border:1px solid #2a3344;
  background:#0b1220;
  border-radius:8px;
  padding:6px 8px;
  font-weight:900;
  color:#e5e7eb !important;
}
.procard .icon.on{outline:2px solid #22c55e}
.procard .apply-btn{
  border:1px solid #2a3344;
  background:#111827;
  border-radius:10px;
  padding:6px 10px;
  font-weight:800;
  color:#e5e7eb !important;
}
.procard .apply-btn:hover{
  background:#1f2937;
}

/* 라이트 테마 대비 */
html[data-theme="light"] .procard .icon,
html[data-theme="light"] .procard .apply-btn{
  background:#e5e7eb;
  color:#0f172a !important;
}

/* 공통 카드 */
.procard .mini{border:1px solid #2a3344;background:#0b1220;border-radius:12px;padding:10px}
.procard .minigrid{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-top:8px}
.procard .mk{font-size:12px;opacity:.85;margin-bottom:4px}
.procard .mv{font-weight:900}
.procard .ms{font-size:12px;color:#9fb1c3;margin-top:4px}

/* Accordion */
.procard .acc{border:1px solid #2a3344;background:#0b1220;border-radius:12px;padding:8px;margin-top:8px;position:relative}
.procard .acc>summary{cursor:pointer;font-weight:800}

/* kv 리스트: 카드형 + 줄분리 */
.procard .kv{margin:0;padding:0;list-style:none;display:flex;flex-direction:column;gap:8px}
.procard .kv-block{display:flex;flex-direction:column;background:#020617;border:1px solid #1f2937;border-radius:8px;padding:8px 10px;line-height:1.8}
.procard .kv-label{font-size:11px;font-weight:700;color:#9fb1c3;margin-bottom:2px;text-transform:uppercase;letter-spacing:.04em}
.procard .kv-body{font-size:13px;display:flex;flex-direction:column;gap:3px;white-space:normal}
.procard .kv-note{font-size:12px;color:#e5e7eb}

/* Dialogue */
.dialog{display:grid;gap:8px;margin-top:6px}
.drow{display:flex;gap:8px;align-items:flex-start}
.dw{font-weight:900;min-width:64px}
.drow.analyst .dw{color:#22c55e}
.drow.you .dw{color:#60a5fa}
.dt{line-height:1.6}

/* TF Snapshot (white cards) */
.procard .tfgrid{margin:-2px 0 6px}
.procard .tfwrap{display:grid;grid-template-columns:repeat(2,1fr);gap:8px}
.procard .tfcard{background:#ffffff;color:#0b1220;border:1px solid #e5e7eb;border-radius:12px;padding:10px}
.procard .tfrow{display:flex;align-items:center;justify-content:space-between;margin-bottom:6px}
.procard .tfl{font-size:14px}
.procard .pill{display:inline-flex;align-items:center;gap:6px;border:1px solid transparent;border-radius:999px;padding:4px 10px;font-weight:900;color:#fff}
.procard .pill.good{background:#16a34a;border-color:#16a34a}
.procard .pill.bad{background:#ef4444;border-color:#ef4444}
.procard .pill.wait{background:#6b7280;border-color:#6b7280}
.procard .bar{height:10px;border-radius:999px;background:#0b1220;border:1px solid #2a3344;overflow:hidden}
.procard .bar.white{background:#f3f4f6;border-color:#e5e7eb}
.procard .fill.good{height:100%;background:#16a34a}
.procard .fill.bad{height:100%;background:#e11d48}
.procard .fill.wait{height:100%;background:#6b7280}
.procard .tfs{display:flex;justify-content:space-between;font-size:12px;margin-top:4px}

/* Snapshot summary */
.procard .tf-summary{ margin:8px 0 2px }

/* i18n color contrast for volatility/band */
.procard .band-color{ color:#fff; }
html[data-theme="light"] .procard .band-color{ color:#facc15; }

/* Volume Walls 카드형 */
.wall-list{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;margin:8px 0 0;padding:0;list-style:none}
.wall-card{border:1px solid #2a3344;border-radius:10px;padding:6px 8px;background:#020617;font-size:12px;line-height:1.8;display:flex;flex-direction:column;justify-content:space-between}
.wall-row{display:flex;justify-content:space-between;align-items:center}
.wall-rank{font-weight:900;color:#22c55e}
.wall-price{font-weight:800}
.wall-sub{margin-top:2px;font-size:11px;color:#9fb1c3}
.wall-vol{font-weight:800}

/* Footer disclaimer */
.footer-disclaimer{font-size:11px;color:#94a3b8;margin-top:10px;padding-top:8px;border-top:1px dashed #1e293b;text-align:center;line-height:1.6}

/* Responsive */
@media (max-width:860px){
  .procard .minigrid{grid-template-columns:repeat(2,1fr)}
}
@media (max-width:560px){
  .procard .minigrid{grid-template-columns:repeat(2,1fr)}
  .wall-list{grid-template-columns:1fr}
  .procard .kv-block{font-size:12px;line-height:1.7}
  .footer-disclaimer{font-size:10px}
}
`;
